import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  catchError,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  merge,
  startWith,
  Subject,
  switchMap,
  tap
} from 'rxjs';

import { ICampaignGlobalWinner, PromotionsService } from 'core';
import { AvatarService } from 'core/services/avatar.service';
import { GoBackComponent, KorviuLoaderComponent, KorviuNoDataComponent } from 'shared';
import { BreadcrumbComponent } from '../../../blocks/breadcrumb/breadcrumb.component';

interface IPromotionWinnerViewModel extends ICampaignGlobalWinner {
  viewId: string;
  position: number;
  name: string;
  avatarUrl: string | null;
}

const WINNERS_PAGE_SIZE = 10;

@Component({
  selector: 'app-promotion-winners',
  imports: [
    BreadcrumbComponent,
    DatePipe,
    GoBackComponent,
    KorviuLoaderComponent,
    KorviuNoDataComponent,
    TranslatePipe
  ],
  templateUrl: './promotion-winners.component.html',
  styleUrl: './promotion-winners.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PromotionWinnersComponent implements OnInit {
  readonly #avatarService = inject(AvatarService);
  readonly #destroyRef = inject(DestroyRef);
  readonly #promotionsService = inject(PromotionsService);
  readonly #translateService = inject(TranslateService);
  readonly #reloadWinners$ = new Subject<number>();

  readonly winners = signal<ICampaignGlobalWinner[]>([]);
  readonly isLoading = signal<boolean>(false);
  readonly loadError = signal<boolean>(false);
  readonly currentPage = signal<number>(1);
  readonly totalPages = signal<number>(1);
  readonly rankedWinners = computed<IPromotionWinnerViewModel[]>(() => {
    const offset = (this.currentPage() - 1) * WINNERS_PAGE_SIZE;

    return this.winners().map((winner, index) => ({
      ...winner,
      viewId: `${winner.campaign_id}-${winner.claimed_at}-${winner.reward.rank ?? 'unranked'}-${index}`,
      position: offset + index + 1,
      name: [winner.participant.first_name, winner.participant.last_initial].filter(Boolean).join(' '),
      avatarUrl: winner.participant.avatar ? this.#avatarService.getUserAvatar(winner.participant.avatar) : null
    }));
  });

  public ngOnInit(): void {
    merge(
      this.#reloadWinners$,
      this.#translateService.onLangChange.pipe(
        map(event => event.lang),
        startWith(this.#translateService.currentLang || this.#translateService.defaultLang || 'es'),
        distinctUntilChanged(),
        map(() => this.currentPage())
      )
    )
      .pipe(
        switchMap(page => {
          this.isLoading.set(true);
          this.loadError.set(false);

          return this.#promotionsService.getGlobalCampaignWinners({ page, pageSize: WINNERS_PAGE_SIZE }).pipe(
            tap(response => {
              this.winners.set(response.results);
              this.currentPage.set(page);
              this.totalPages.set(Math.max(1, response.total_pages));
            }),
            catchError(() => {
              this.winners.set([]);
              this.loadError.set(true);
              return EMPTY;
            }),
            finalize(() => this.isLoading.set(false))
          );
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe();
  }

  public loadWinners(page: number): void {
    if (page < 1 || page > this.totalPages()) {
      return;
    }

    this.#reloadWinners$.next(page);
  }
}
